import { AccountType, deepLinkRouteParamOptions, EnabledFeatures, Equipment, IEnabledFeatures } from 'fitbeat.models';
import { Platform } from 'react-native';
import { createSelector } from 'reselect';
import { IAppStore } from '../HomeWorkouts';
import { Logger } from '../utils';

export const shouldNavigateToEliteOnBoardingSelector = createSelector(
  [
    ({deepLinkParamaters}) => deepLinkParamaters.onboarding,
    ({deepLinkParamaters}) => deepLinkParamaters.pendingPlanId,
    ({deepLinkParamaters}) => deepLinkParamaters.planId,
  ],
  (onboarding, planId, pendingPlanId) => !!(onboarding === 'elite' && planId && pendingPlanId),
);

export const shouldNavigateToSocialAuthLoadingSelector = createSelector(
  [
    ({deepLinkParamaters}) => deepLinkParamaters.type,
    ({deepLinkParamaters}) => deepLinkParamaters.providerId,
  ],
  (type, providerId) => !!(type && providerId),
);

const appOpeningRoute = () => {
  return 'Welcome';
};

export const preAuthRouteSelector = createSelector(
  [
    ({deepLinkParamaters}) => deepLinkParamaters.wasDeepLink,
    ({deepLinkParamaters}) => deepLinkParamaters.route,
    ({deepLinkParamaters}) => deepLinkParamaters.loginWithToken,
    ({deepLinkParamaters}) => deepLinkParamaters.errorMessage,
    ({deepLinkParamaters}) => deepLinkParamaters.accountType,
    ({deepLinkParamaters}) => deepLinkParamaters.workoutInviteId,
    ({deepLinkParamaters}) => deepLinkParamaters.invitationCode,
    ({deepLinkParamaters}) => deepLinkParamaters.email,
    ({deepLinkParamaters}) => deepLinkParamaters.gymName,
    ({deepLinkParamaters}) => deepLinkParamaters.onboarding,
    ({deepLinkParamaters}) => deepLinkParamaters.pendingPlanId,
    ({deepLinkParamaters}) => deepLinkParamaters.planId,
    ({deepLinkParamaters}) => deepLinkParamaters.continueSignupToken,
    ({deepLinkParamaters}) => deepLinkParamaters.existingUserLogin,
    ({deepLinkParamaters}) => deepLinkParamaters.specialOfferId,
  ],
  (
    wasDeepLink,
    route,
    loginWithToken,
    errorMessage,
    accountType,
    workoutInviteId,
    invitationCode,
    email,
    gymName,
    onboarding,
    pendingPlanId,
    planId,
    continueSignupToken,
    existingUserLogin,
    specialOfferId,
  ) => {
    if (wasDeepLink) {
      if (existingUserLogin) {
        return 'Login';
      }
      if (route === deepLinkRouteParamOptions.subscription && specialOfferId) {
        return 'PreAuthPayment';
      }
      if (route === 'install-app' && Platform.OS === 'web') {
        return 'HomeInstallAppScreen';
      }
      const routeIsDemoStudio = route === 'demo-studio-workout-booking';
      const shouldNavigateToCreatePreAccount = routeIsDemoStudio && gymName;
      if (shouldNavigateToCreatePreAccount) {
        return 'CreatePreAccount';
      }
      if (loginWithToken) {
        Logger.debug('[AuthLoadingScreen] explicitly go to auth navigator/welcome to loginWithToken');
        return 'Welcome';
      }
      if (invitationCode) {
        return 'InvitationCodeSignup';
      }
      const shouldNavigateToEliteOnBoarding = !!(onboarding === 'elite' && planId && pendingPlanId);
      if (shouldNavigateToEliteOnBoarding) {
        return 'CreateEliteAccount';
      }
      if (continueSignupToken) {
        if (Platform.OS === 'web') {
          return 'HomeInstallAppScreen';
        }
        return 'PreAccountSetPassword';
      }
      let navigationRoute = '';
      // todo: see if this can still rely on a home account type
      if (accountType === AccountType.Home) {
        if (workoutInviteId) {
          navigationRoute = 'InviteToWorkout';
        } else {
          navigationRoute = appOpeningRoute();
        }
      } else if (accountType === AccountType.Studio) {
        navigationRoute = 'SignUp';
      } else {
        navigationRoute = appOpeningRoute();
      }
      return navigationRoute;
    }
  },
);

export const getEnabledFeaturesFromState = (state: IAppStore) => state.auth.user.enabledFeatures;

export const enabledFeaturesSelector = createSelector(
  [getEnabledFeaturesFromState],
  (enabledFeatures) => {
    if (!enabledFeatures) {
      throw new Error('No enabled features loaded for user');
    }

    return enabledFeatures;
  },
);

const getGarminIsEnabledFromState = (state: IAppStore) => state.auth.user.garminIsEnabledForUser;

export const garminIsEnabledForUserSelector = createSelector(
  [getGarminIsEnabledFromState],
  (garminIsEnabled) => {
    return garminIsEnabled;
  },
);

export const getDeepLinkParamsFromState = (state: IAppStore) => state.deepLinkParamaters;

export const deepLinkParamsSelector = createSelector(
  [getDeepLinkParamsFromState],
  (deepLinkParamaters) => {
    return deepLinkParamaters;
  },
);

export const isInitialDataLoadedSelector = createSelector(
    [(state: IAppStore) => state.auth.user.isInitialDataLoaded],
    (isInitialDataLoaded) => {
       return isInitialDataLoaded;
    },
);

export const isLoadingEnabledFeaturesSelector: (state: IAppStore) => boolean  = createSelector(
  [(state: IAppStore) => state.auth.user.loadingEnabledFeatures],
  (loadingEnabledFeatures) => loadingEnabledFeatures,
);

export const hasAccessToAStudioGymSelector: (state: IAppStore) => boolean = createSelector(
  [(state: IAppStore) => state.auth.user.enabledFeatures],
  (enabledFeatures) => !!enabledFeatures && EnabledFeatures.hasAccessToAStudioGym(enabledFeatures),
);
